

import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../Common/Configurations/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import apiService from "../../Common/Configurations/APIService";
import config from "../../Common/Configurations/APIConfig";
import Header from "../../Common/Layout/Header";
import ClientTabs from "../Clients/ClientTabs";
import { format } from "date-fns";
import Modal from "react-bootstrap/Modal";
import currencyFlags from "../../Data/CurrencyFlags.json";
import DeleteAlert from "../../Common/Layout/DeleteAlert";

// Supplier types constant
const supplierTypes = [
  "FLIGHT",
  "HOTEL",
  "PACKAGES",
  "BUS",
  "TRAINS",
  "CAR",
  "TRANSFER",
  "SIGHTSEEING",
  "VISA",
];

const B2ECompanies = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  // Filter states
  const [planTypeFilter, setPlanTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Edit/Add modal states
  const [editShow, setEditShow] = useState(false);
  const [editClient, setEditClient] = useState({});
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [editBtnLoading, setEditBtnLoading] = useState(false);

  // Validation states
  const [formErrors, setFormErrors] = useState({});

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // Countries and currencies state
  const [countries, setCountries] = useState([]);
  const [currencies, setCurrencies] = useState([]);

  // API Modal states
  const [apiShow, setAPIShow] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [selectedSupplierType, setSelectedSupplierType] = useState("FLIGHT");
  const [supplierForm, setSupplierForm] = useState({});
  const [apiErrors, setApiErrors] = useState({});
  const [apiBtnLoading, setApiBtnLoading] = useState(false);

  // Process countries data from currencyFlags.json
  useEffect(() => {
    if (currencyFlags && Array.isArray(currencyFlags)) {
      const sortedCountries = [...currencyFlags].sort((a, b) =>
        a.country.localeCompare(b.country)
      );
      setCountries(sortedCountries);

      const uniqueCurrencies = [];
      const currencyMap = new Map();

      sortedCountries.forEach((country) => {
        if (country.code && !currencyMap.has(country.code)) {
          currencyMap.set(country.code, true);
          uniqueCurrencies.push({
            code: country.code,
            name: country.currency || "",
          });
        }
      });

      uniqueCurrencies.sort((a, b) => a.code.localeCompare(b.code));
      setCurrencies(uniqueCurrencies);
    }
  }, []);

  // Get clients data
  const GetData = async (currentPage = 1) => {
  if (!user) return;
  setLoading(true);
  try {
    let apiUrl = `${config.GET_B2E_COMPANIES}?companyType=CLIENT&PageNumber=${currentPage}&PageSize=${pageSize}`;

    if (planTypeFilter) apiUrl += `&planType=${planTypeFilter}`;
    if (statusFilter !== "") apiUrl += `&isActive=${statusFilter}`;

    const response = await apiService.getWithParams(apiUrl);
    
    // LOG THIS to see exactly what 'response' looks like
    console.log("API Response:", response);

    // If your response IS the array, use 'response'. 
    // If it's an Axios object, use 'response.data'.
    const clientsData = Array.isArray(response) ? response : (response.data || []);
    
    setClients(clientsData);
    setFilteredClients(clientsData);
    
    // Note: If response is a direct array, these might be undefined 
    // unless the API sends them in headers or a wrapper
    setTotalPages(response.totalPages || 1); 
    setTotalRecords(response.totalRecords || clientsData.length);
  } catch (error) {
    console.error("Error fetching clients:", error);
    setClients([]);
    setFilteredClients([]);
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    if (!authLoading && user) {
      GetData(pageNumber);
    }
  }, [user, authLoading, pageNumber]);

  // Handle filter changes
  const handlePlanTypeFilter = (e) => {
    const value = e.target.value;
    setPlanTypeFilter(value);
    setPageNumber(1);
    GetData(1);
  };

  const handleStatusFilter = (e) => {
    const value = e.target.value;
    setStatusFilter(value);
    setPageNumber(1);
    GetData(1);
  };

  // Reset all filters
  const handleResetFilters = () => {
    setPlanTypeFilter("");
    setStatusFilter("");
    setSearchTerm("");
    setPageNumber(1);
    GetData(1);
  };

  // Generate search suggestions
  const generateSuggestions = (searchValue) => {
    if (!searchValue.trim()) {
      setSuggestions([]);
      return;
    }

    const searchLower = searchValue.toLowerCase();
    const companyMatches = clients
      .filter((client) =>
        client.companyName?.toLowerCase().includes(searchLower)
      )
      .slice(0, 5);

    const brandMatches = clients
      .filter(
        (client) =>
          client.brandName?.toLowerCase().includes(searchLower) &&
          !client.companyName?.toLowerCase().includes(searchLower)
      )
      .slice(0, 3);

    const emailMatches = clients
      .filter(
        (client) =>
          client.email?.toLowerCase().includes(searchLower) &&
          !client.companyName?.toLowerCase().includes(searchLower) &&
          !client.brandName?.toLowerCase().includes(searchLower)
      )
      .slice(0, 2);

    const allSuggestions = [
      ...companyMatches,
      ...brandMatches,
      ...emailMatches,
    ];
    setSuggestions(allSuggestions);
  };

  // Handle search
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    // Generate suggestions
    generateSuggestions(value);
    setShowSuggestions(true);

    if (value.trim() === "") {
      setFilteredClients(clients);
      setSuggestions([]);
      setShowSuggestions(false);
    } else {
      const filtered = clients.filter(
        (client) =>
          client.companyName?.toLowerCase().includes(value.toLowerCase()) ||
          client.brandName?.toLowerCase().includes(value.toLowerCase()) ||
          client.email?.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredClients(filtered);
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (client) => {
    setSearchTerm(client.companyName);
    setFilteredClients([client]);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  // Handle input blur (hide suggestions after a delay)
  const handleInputBlur = () => {
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  };

  // Pagination functions
  const GetFirstPageData = () => {
    GetData(1);
  };

  const GetLastPageData = () => {
    GetData(totalPages);
  };

  const GetPageData = (number) => {
    setPageNumber(number);
    if (pageNumber !== number) GetData(number);
  };

  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  const renderPageNumbers = pageNumbers.map((number) => {
    return (
      <li
        className={`page-item ${pageNumber === number ? "active" : ""}`}
        key={number}
        onClick={() => GetPageData(number)}
      >
        <Link className="page-link">{number}</Link>
      </li>
    );
  });

  // Handle Add Client button click
  const handleAddClient = () => {
    const newClient = {
      companyId: "",
      companyName: "",
      brandName: "",
      email: "",
      phoneNumber: "",
      gstNo: "",
      panCard: "",
      address: "",
      city: "",
      state: "",
      country: "IN",
      currency: "INR",
      primaryDomain: "",
      isActive: true,
      planType: "TRIAL",
      companyType: "CLIENT",
      connects: 0,
      isB2B: false ,
      isB2C: false,
      isB2E: true,
      isWebsite: false,
      isRenewal: false,
      endDate: new Date(),
      whatsAppNumber: "",
      whatsAppPhoneNumberId: "",
      whatsAppPermanentToken: "",
      isChatBot: false,
      isSupplierAPI: false,
    };

    setEditClient(newClient);
    setLogoPreview(null);
    setLogoFile(null);
    setFormErrors({});
    setEditShow(true);
  };

  // Handle edit icon click
  const handleEditClick = (client) => {
    const cleanedData = {};
    Object.keys(client).forEach((key) => {
      if (
        client[key] === null ||
        client[key] === "null" ||
        client[key] === "undefined"
      ) {
        cleanedData[key] = "";
      } else {
        cleanedData[key] = client[key];
      }
    });

    setEditClient(cleanedData);
    setLogoPreview(cleanedData.logoUrl || cleanedData.logoSrc);
    setFormErrors({});
    setEditShow(true);
  };

  // Close edit modal
  const handleEditClose = () => {
    setEditShow(false);
    setEditClient({});
    setLogoFile(null);
    setLogoPreview(null);
    setFormErrors({});
  };

  // Handle file change for logo
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  // Handle country change
  const handleCountryChange = (countryCode) => {
    const selectedCountry = countries.find(
      (country) => country.countryCode === countryCode
    );
    setEditClient({
      ...editClient,
      country: countryCode,
      currency: selectedCountry?.code || "",
    });
  };

  // Handle currency change
  const handleCurrencyChange = (currencyCode) => {
    setEditClient({
      ...editClient,
      currency: currencyCode,
    });
  };

  // Check if country is India
  const isIndia = (countryCode) => {
    return countryCode === "IN";
  };

  // Handle input change for edit form
  const handleEditInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldsToUppercase = [
      "companyName",
      "brandName",
      "city",
      "state",
      "gstNo",
      "panCard",
    ];

    setEditClient({
      ...editClient,
      [name]:
        type === "checkbox"
          ? checked
          : fieldsToUppercase.includes(name)
          ? value.toUpperCase()
          : value,
    });

    // Clear the specific error when user starts typing
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: null,
      });
    }
  };

  // Validation logic for the client form
  const validateClientForm = () => {
    const newErrors = {};

    // Company Name validation
    if (!editClient.companyName || editClient.companyName.trim() === "") {
      newErrors.companyName = "Company Name is required.";
    }

    // Brand Name validation
    if (!editClient.brandName || editClient.brandName.trim() === "") {
      newErrors.brandName = "Brand Name is required.";
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!editClient.email || !emailRegex.test(editClient.email)) {
      newErrors.email = "Please enter a valid email address.";
    }

    // Phone Number validation
    const phoneRegex = /^\d{10,15}$/;
    if (!editClient.phoneNumber || !phoneRegex.test(editClient.phoneNumber)) {
      newErrors.phoneNumber =
        "Please enter a valid phone number (10-15 digits).";
    }

    setFormErrors(newErrors);
    // Return true if there are no errors
    return Object.keys(newErrors).length === 0;
  };

  // Handle update client
  const handleUpdateClient = async (e) => {
    e.preventDefault();
    if (validateClientForm()) {
      setEditBtnLoading(true);

      try {
        if (!editClient.companyId) {
          const newClientData = {
            companyName: editClient.companyName || "",
            brandName: editClient.brandName || "",
            email: editClient.email || "",
            phoneNumber: editClient.phoneNumber || "",
            gstNo: editClient.gstNo || "",
            panCard: editClient.panCard || "",
            address: editClient.address || "",
            city: editClient.city || "",
            state: editClient.state || "",
            country: editClient.country || "",
            currency: editClient.currency || "",
            companyType: "CLIENT",
            parentCompanyId: user?.cid,
            isActive: editClient.isActive,
            planType: "TRIAL",
            primaryDomain: editClient.primaryDomain || "",
            connects: editClient.connects || 0,
            isB2B: editClient.isB2B || false,
            isB2C: editClient.isB2C || false,
            isB2E: editClient.isB2E || false,
            isWebsite: editClient.isWebsite || false,
            isRenewal: editClient.isRenewal || false,
            endDate: editClient.endDate || new Date(),
            whatsAppNumber: editClient.whatsAppNumber || "",
            whatsAppPhoneNumberId: editClient.whatsAppPhoneNumberId || "",
            whatsAppPermanentToken: editClient.whatsAppPermanentToken || "",
            isChatBot: editClient.isChatBot || false,
            isSupplierAPI: editClient.isSupplierAPI || false,
            userType: "CLIENT",
            password: "App@000",
          };

          const response = await apiService.create(
            config.CREATECOMPANYBYSUPERADMIN,
            newClientData
          );

          if (response && response.success) {
            await GetData(pageNumber);
            handleEditClose();
          } else {
            console.error("Failed to create client");
          }
        } else {
          const formData = new FormData();

          formData.append("companyId", editClient.companyId);
          formData.append("companyName", editClient.companyName || "");
          formData.append("brandName", editClient.brandName || "");
          formData.append("email", editClient.email || "");
          formData.append("phoneNumber", editClient.phoneNumber || "");
          formData.append("gstNo", editClient.gstNo || "");
          formData.append("panCard", editClient.panCard || "");
          formData.append("address", editClient.address || "");
          formData.append("city", editClient.city || "");
          formData.append("state", editClient.state || "");
          formData.append("country", editClient.country || "");
          formData.append("currency", editClient.currency || "");
          formData.append("primaryDomain", editClient.primaryDomain || "");
          formData.append("isActive", editClient.isActive);
          formData.append("planType", editClient.planType || "");
          formData.append("connects", editClient.connects || 0);
          formData.append("isB2B", editClient.isB2B || false);
          formData.append("isB2C", editClient.isB2C || false);
          formData.append("isB2E", editClient.isB2E || false);
          formData.append("isWebsite", editClient.isWebsite || false);
          formData.append("isRenewal", editClient.isRenewal || false);
          formData.append("endDate", editClient.endDate || "");
          formData.append("whatsAppNumber", editClient.whatsAppNumber || "");
          formData.append(
            "whatsAppPhoneNumberId",
            editClient.whatsAppPhoneNumberId || ""
          );
          formData.append(
            "whatsAppPermanentToken",
            editClient.whatsAppPermanentToken || ""
          );
          formData.append("isChatBot", editClient.isChatBot || false);
          formData.append("isSupplierAPI", editClient.isSupplierAPI || false);

          if (logoFile) {
            formData.append("logoFile", logoFile);
          }

          await apiService.update(config.UPDATECOMPANYBYSUPERADMIN, formData);
          await GetData(pageNumber);
          handleEditClose();
        }
      } catch (error) {
        console.error("Error updating client:", error);
      } finally {
        setEditBtnLoading(false);
      }
    }
  };

  // Delete functionality
  const handleOpenDeleteModal = (clientId) => {
    setItemToDelete(clientId);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  const handleDelete = async () => {
    if (itemToDelete !== null) {
      try {
        await apiService.remove(config.DELETECOMPANY, itemToDelete);
        await GetData(pageNumber);
      } catch (error) {
        console.error("Error deleting client:", error);
      }
      handleCloseDeleteModal();
    }
  };

  // API Modal Functions
  const handleAPIClose = () => {
    setAPIShow(false);
    setSelectedClient(null);
    setSuppliers([]);
    setSupplierForm({});
    setSelectedSupplierType("FLIGHT");
    setApiErrors({});
  };

  // Initialize supplier form function
  const initializeSupplierForm = (supplierType, existingData = null) => {
    const baseForm = {
      supplierId: "00000000-0000-0000-0000-000000000000",
      supplierCompanyId: existingData?.supplierCompanyId || "",
      isActive:
        existingData?.isActive !== undefined ? existingData.isActive : true,
      apiType: existingData?.apiType || "DEVELOPMENT",
      devAgentCode: existingData?.devAgentCode || "",
      devapiurl: existingData?.devapiurl || "",
      devapikey: existingData?.devapikey || "",
      devusername: existingData?.devusername || "",
      devpassword: existingData?.devpassword || "",
      prodAgentCode: existingData?.prodAgentCode || "",
      prodapiurl: existingData?.prodapiurl || "",
      prodapikey: existingData?.prodapikey || "",
      produsername: existingData?.produsername || "",
      prodpassword: existingData?.prodpassword || "",
      type: supplierType,
      companyId: user?.cid || "",
    };

    if (
      existingData &&
      existingData.supplierId &&
      existingData.supplierId !== "00000000-0000-0000-0000-000000000000"
    ) {
      return {
        ...baseForm,
        supplierId: existingData.supplierId,
        // Preserve all existing data but update the type
        type: supplierType,
      };
    }

    return baseForm;
  };

  // Get all suppliers for dropdown - FIXED API CALL
  const getAllSuppliers = async () => {
    try {
      // Using GET method instead of POST for fetching data
      const response = await apiService.getAll(
        `${config.GET_B2E_COMPANIES}?companyType=SUPPLIER`
      );
      return response.data || [];
    } catch (error) {
      console.error("Error fetching suppliers:", error);
      return [];
    }
  };

  // Fetch supplier keys for API modal - FIXED API CALL
  const getSupplierKeys = async (
    clientCompanyId,
    supplierCompanyId,
    supplierType
  ) => {
    setLoadingSuppliers(true);
    try {
      // Based on your API, this should be a GET request with query parameters
      const response = await apiService.getAll(
        `${config.GETSUPPLIERKEYSFORCLIENTCOMPANY}?ClientCompanyId=${clientCompanyId}&SupplierCompanyId=${supplierCompanyId}&SupplierType=${supplierType}`
      );

      if (response && response.suppliers && response.suppliers.length > 0) {
        return response.suppliers[0];
      }
      return null;
    } catch (error) {
      console.error("Error fetching supplier keys:", error);
      return null;
    } finally {
      setLoadingSuppliers(false);
    }
  };

  // Handle Key icon click
  const handleKeyClick = async (client) => {
    setSelectedClient(client);
    setAPIShow(true);

    // Load all suppliers for dropdown
    try {
      const allSuppliers = await getAllSuppliers();
      setSuppliers(allSuppliers);

      // Initialize empty form
      setSupplierForm(initializeSupplierForm(selectedSupplierType));
    } catch (error) {
      console.error("Error loading suppliers:", error);
      setSuppliers([]);
    }
  };

  // Handle supplier selection change
  const handleSupplierChange = async (supplierCompanyId) => {
    if (supplierCompanyId && selectedClient) {
      setSupplierForm((prev) => ({
        ...prev,
        supplierCompanyId: supplierCompanyId,
      }));

      // Fetch supplier keys for the selected supplier and current type
      const supplierKeys = await getSupplierKeys(
        selectedClient.companyId,
        supplierCompanyId,
        selectedSupplierType
      );

      if (supplierKeys) {
        setSupplierForm(
          initializeSupplierForm(selectedSupplierType, supplierKeys)
        );
      } else {
        // If no existing configuration, initialize empty form with selected supplier and current type
        setSupplierForm(
          initializeSupplierForm(selectedSupplierType, {
            supplierCompanyId: supplierCompanyId,
          })
        );
      }
    } else {
      // If no supplier selected, reset but keep the current type
      setSupplierForm(initializeSupplierForm(selectedSupplierType));
    }
  };

  // Handle supplier type change
  const handleSupplierTypeChange = async (type) => {
    setSelectedSupplierType(type);

    // If a supplier is already selected, fetch data for the new type with the same supplier
    if (supplierForm.supplierCompanyId && selectedClient) {
      const supplierKeys = await getSupplierKeys(
        selectedClient.companyId,
        supplierForm.supplierCompanyId,
        type
      );

      if (supplierKeys) {
        // If data exists for this supplier + type combination, populate the form
        setSupplierForm(initializeSupplierForm(type, supplierKeys));
      } else {
        // If no data exists, reset the form fields but keep the supplier company ID
        setSupplierForm(
          initializeSupplierForm(type, {
            supplierCompanyId: supplierForm.supplierCompanyId,
          })
        );
      }
    } else {
      // If no supplier is selected, just update the type
      setSupplierForm((prev) => ({
        ...prev,
        type: type,
      }));
    }
  };

  // Handle input change for supplier form
  const handleSupplierInputChange = (field, value) => {
    setSupplierForm((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear API errors when user starts typing
    if (apiErrors[field]) {
      setApiErrors({
        ...apiErrors,
        [field]: null,
      });
    }
  };

  // Form submission for API modal
  const handleApiSubmit = async (e) => {
    e.preventDefault();
    setApiBtnLoading(true);

    try {
      // Validate required fields
      if (!supplierForm.supplierCompanyId) {
        setApiErrors({ supplierCompanyId: "Please select a supplier company" });
        setApiBtnLoading(false);
        return;
      }

      const submitData = {
        supplierId: supplierForm.supplierId,
        supplierCompanyId: supplierForm.supplierCompanyId,
        isActive: supplierForm.isActive,
        apiType: supplierForm.apiType,
        DEVAgentCode: supplierForm.devAgentCode || "",
        DEVAPIURL: supplierForm.devapiurl || "",
        DEVAPIKEY: supplierForm.devapikey || "",
        DEVUSERNAME: supplierForm.devusername || "",
        DEVPASSWORD: supplierForm.devpassword || "",
        PRODAgentCode: supplierForm.prodAgentCode || "",
        PRODAPIURL: supplierForm.prodapiurl || "",
        PRODAPIKEY: supplierForm.prodapikey || "",
        PRODUSERNAME: supplierForm.produsername || "",
        PRODPASSWORD: supplierForm.prodpassword || "",
        type: supplierForm.type,
        companyId: selectedClient?.companyId,
      };

      const isNew =
        submitData.supplierId === "00000000-0000-0000-0000-000000000000";

      try {
        if (isNew) {
          await apiService.create(config.CREATESUPPLIER, submitData);
        } else {
          await apiService.update(config.UPDATESUPPLIER, submitData);
        }

        handleAPIClose();
        // Show success message or refresh data if needed
      } catch (error) {
        console.error("Error saving supplier API:", error);
        setApiErrors({ submit: "Failed to save API Keys" });
      }
    } catch (error) {
      console.error("Error in API submission:", error);
      setApiErrors({ submit: "An error occurred while saving" });
    } finally {
      setApiBtnLoading(false);
    }
  };

  // Render supplier form for API modal
  const renderSupplierForm = () => {
    return (
      <div className="row">
        {/* Supplier Selection */}
        <div className="col-lg-6 mb-3">
          <label className="form-label">Supplier Company *</label>
          <select
            value={supplierForm.supplierCompanyId || ""}
            onChange={(e) => handleSupplierChange(e.target.value)}
            className={`form-select ${
              apiErrors.supplierCompanyId ? "is-invalid" : ""
            }`}
          >
            <option value="">Select Supplier</option>
            {suppliers.map((supplier) => (
              <option key={supplier.companyId} value={supplier.companyId}>
                {supplier.brandName || supplier.companyName}
              </option>
            ))}
          </select>
          {apiErrors.supplierCompanyId && (
            <div className="invalid-feedback">
              {apiErrors.supplierCompanyId}
            </div>
          )}
        </div>

        {/* Supplier Type */}
        <div className="col-lg-6 mb-3">
          <label className="form-label">Supplier Type</label>
          <select
            value={selectedSupplierType}
            onChange={(e) => handleSupplierTypeChange(e.target.value)}
            className="form-select"
          >
            {supplierTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>

        {/* API Type */}
        <div className="col-lg-6 mb-3">
          <label className="form-label">API Type</label>
          <select
            value={supplierForm.apiType || "DEVELOPMENT"}
            onChange={(e) =>
              handleSupplierInputChange("apiType", e.target.value)
            }
            className="form-select"
          >
            <option value="DEVELOPMENT">DEVELOPMENT</option>
            <option value="PRODUCTION">PRODUCTION</option>
          </select>
        </div>

        {/* Status */}
        <div className="col-lg-6 mb-3">
          <label className="form-label">Status</label>
          <select
            value={
              supplierForm.isActive !== undefined ? supplierForm.isActive : true
            }
            onChange={(e) =>
              handleSupplierInputChange("isActive", e.target.value === "true")
            }
            className="form-select"
          >
            <option value={true}>Active</option>
            <option value={false}>Inactive</option>
          </select>
        </div>

        {/* Development API Configuration */}
        <div className="col-12 mb-3 mt-4">
          <h6 className="border-bottom pb-2 text-primary font-weight-bold">
            Development API Keys
          </h6>
        </div>

        <div className="col-lg-4 mb-3">
          <label className="form-label">DEV Agent Code</label>
          <input
            type="text"
            value={supplierForm.devAgentCode || ""}
            onChange={(e) =>
              handleSupplierInputChange("devAgentCode", e.target.value)
            }
            className="form-control"
            placeholder="Enter development agent code"
          />
        </div>

        <div className="col-lg-8 mb-3">
          <label className="form-label">DEV API URL</label>
          <input
            type="text"
            value={supplierForm.devapiurl || ""}
            onChange={(e) =>
              handleSupplierInputChange("devapiurl", e.target.value)
            }
            className="form-control"
            placeholder="Enter development API URL"
          />
        </div>

        <div className="col-lg-6 mb-3">
          <label className="form-label">DEV API KEY</label>
          <input
            type="text"
            value={supplierForm.devapikey || ""}
            onChange={(e) =>
              handleSupplierInputChange("devapikey", e.target.value)
            }
            className="form-control"
            placeholder="Enter development API key"
          />
        </div>

        <div className="col-lg-6 mb-3">
          <label className="form-label">DEV API USERNAME</label>
          <input
            type="text"
            value={supplierForm.devusername || ""}
            onChange={(e) =>
              handleSupplierInputChange("devusername", e.target.value)
            }
            className="form-control"
            placeholder="Enter development username"
          />
        </div>

        <div className="col-lg-6 mb-3">
          <label className="form-label">DEV API PASSWORD</label>
          <input
            type="password"
            value={supplierForm.devpassword || ""}
            onChange={(e) =>
              handleSupplierInputChange("devpassword", e.target.value)
            }
            className="form-control"
            placeholder="Enter development password"
          />
        </div>

        {/* Production API Configuration */}
        <div className="col-12 mb-3 mt-4">
          <h6 className="border-bottom pb-2 text-primary font-weight-bold">
            Production API Keys
          </h6>
        </div>

        <div className="col-lg-4 mb-3">
          <label className="form-label">PROD Agent Code</label>
          <input
            type="text"
            value={supplierForm.prodAgentCode || ""}
            onChange={(e) =>
              handleSupplierInputChange("prodAgentCode", e.target.value)
            }
            className="form-control"
            placeholder="Enter production agent code"
          />
        </div>

        <div className="col-lg-8 mb-3">
          <label className="form-label">PROD API URL</label>
          <input
            type="text"
            value={supplierForm.prodapiurl || ""}
            onChange={(e) =>
              handleSupplierInputChange("prodapiurl", e.target.value)
            }
            className="form-control"
            placeholder="Enter production API URL"
          />
        </div>

        <div className="col-lg-6 mb-3">
          <label className="form-label">PROD API KEY</label>
          <input
            type="text"
            value={supplierForm.prodapikey || ""}
            onChange={(e) =>
              handleSupplierInputChange("prodapikey", e.target.value)
            }
            className="form-control"
            placeholder="Enter production API key"
          />
        </div>

        <div className="col-lg-6 mb-3">
          <label className="form-label">PROD API USERNAME</label>
          <input
            type="text"
            value={supplierForm.produsername || ""}
            onChange={(e) =>
              handleSupplierInputChange("produsername", e.target.value)
            }
            className="form-control"
            placeholder="Enter production username"
          />
        </div>

        <div className="col-lg-6 mb-3">
          <label className="form-label">PROD API PASSWORD</label>
          <input
            type="password"
            value={supplierForm.prodpassword || ""}
            onChange={(e) =>
              handleSupplierInputChange("prodpassword", e.target.value)
            }
            className="form-control"
            placeholder="Enter production password"
          />
        </div>

        {/* Submit Error */}
        {apiErrors.submit && (
          <div className="col-12 mb-3">
            <div className="alert alert-danger mb-0">{apiErrors.submit}</div>
          </div>
        )}
      </div>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return format(new Date(dateString), "dd MMM, yyyy hh:mm a");
  };

  if (authLoading) return <p>Loading...</p>;
  if (!user) {
    navigate("/");
    return null;
  }

  return (
    <>
      <Header />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="card">
              <div className="p-2">
                <ClientTabs pageName="B2E" />
              </div>

              <div className="card-body">
                <div className="row mb-4">
                  <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <h5 className="card-title mb-0"> B2E Client Companies</h5>
                      <button
                        className="btn btn-primary"
                        onClick={handleAddClient}
                      >
                        <i className="ri-add-line me-2"></i>
                        Add B2E Client
                      </button>
                    </div>

                    {/* Filter Section */}
                    <div
                      className="row align-items-end mb-4"
                      style={{ gap: "8px 0" }}
                    >
                      {/* Search Bar - wider column */}
                      <div className="col-md-6">
                        <div style={{ position: "relative" }}>
                          <div style={{ position: "relative" }}>
                            <i
                              className="ri-search-line"
                              style={{
                                position: "absolute",
                                left: "12px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                zIndex: 10,
                                color: "#6c757d",
                              }}
                            ></i>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Search by company name, brand name, or email..."
                              value={searchTerm}
                              onChange={handleSearch}
                              onFocus={() => setShowSuggestions(true)}
                              onBlur={handleInputBlur}
                              style={{
                                height: "45px",
                                paddingLeft: "40px",
                                position: "relative",
                              }}
                            />
                            {searchTerm && (
                              <button
                                className="btn position-absolute end-0 top-0 bottom-0"
                                style={{
                                  background: "transparent",
                                  border: "none",
                                  zIndex: 10,
                                }}
                                onClick={() => setSearchTerm("")}
                              >
                                <i className="ri-close-line fs-5"></i>
                              </button>
                            )}

                            {/* Suggestions dropdown */}
                            {showSuggestions && suggestions.length > 0 && (
                              <div
                                style={{
                                  position: "absolute",
                                  width: "100%",
                                  marginTop: "4px",
                                  border: "1px solid #dee2e6",
                                  backgroundColor: "white",
                                  borderRadius: "6px",
                                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                  zIndex: 1000,
                                }}
                              >
                                {suggestions.map((client) => (
                                  <div
                                    key={client.companyId}
                                    style={{
                                      padding: "8px 12px",
                                      display: "flex",
                                      alignItems: "center",
                                      cursor: "pointer",
                                      backgroundColor: "white",
                                      transition: "background-color 0.2s",
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.backgroundColor =
                                        "#f8f9fa";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.backgroundColor = "white";
                                    }}
                                    onClick={() =>
                                      handleSuggestionClick(client)
                                    }
                                  >
                                    <i className="ri-building-2-line me-2 text-secondary"></i>
                                    <div>
                                      <div style={{ fontWeight: "600" }}>
                                        {client.companyName}
                                      </div>
                                      {client.brandName &&
                                        client.brandName !==
                                          client.companyName && (
                                          <div
                                            style={{
                                              fontSize: "0.875rem",
                                              color: "#6c757d",
                                            }}
                                          >
                                            {client.brandName}
                                          </div>
                                        )}
                                      {client.email && (
                                        <div
                                          style={{
                                            fontSize: "0.875rem",
                                            color: "#6c757d",
                                          }}
                                        >
                                          {client.email}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Plan Type Filter */}
                      <div className="col-md-2">
                        <label className="form-label">Plan Type</label>
                        <select
                          className="form-select"
                          value={planTypeFilter}
                          onChange={handlePlanTypeFilter}
                          style={{ height: "45px" }}
                        >
                          <option value="">All Plans</option>
                          <option value="TRIAL">TRIAL</option>
                          <option value="MONTHLYSTARTER">MONTHLYSTARTER</option>
                          <option value="MONTHLYPRO">MONTHLYPRO</option>
                          <option value="MONTHLYENTERPRISE">
                            MONTHLYENTERPRISE
                          </option>
                        </select>
                      </div>

                      {/* Status Filter */}
                      <div className="col-md-2">
                        <label className="form-label">Status</label>
                        <select
                          className="form-select"
                          value={statusFilter}
                          onChange={handleStatusFilter}
                          style={{ height: "45px" }}
                        >
                          <option value="">All Status</option>
                          <option value="true">Active</option>
                          <option value="false">Inactive</option>
                        </select>
                      </div>

                      {/* Reset Button */}
                      <div className="col-md-1">
                        <button
                          className="btn btn-danger w-100"
                          onClick={handleResetFilters}
                          style={{ height: "45px" }}
                        >
                          <i className="ri-refresh-line me-1"></i>
                          Reset
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Table Section with proper gap */}
                <div className="mt-4">
                  <div className="table-responsive table-card">
                    <table className="table align-middle table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>S.No</th>
                          <th>Company / Brand</th>
                          <th>Contact</th>
                          <th>Plan</th>
                          <th>Connects Left</th>
                          <th>Expires On</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr>
                            <td colSpan="8" className="text-center py-4">
                              Loading B2E clients...
                            </td>
                          </tr>
                        ) : filteredClients.length > 0 ? (
                          filteredClients.map((client, index) => (
                            <tr key={client.companyId}>
                              <td>{index + 1}</td>
                              <td>
                                <div>
                                  <strong>{client.companyName}</strong>
                                </div>
                                <small className="text-muted">
                                  {client.brandName || "-"}
                                </small>
                              </td>
                              <td>
                                <div>{client.email}</div>
                                <small className="text-muted">
                                  {client.phoneNumber}
                                </small>
                              </td>
                              <td>
                                <span className="badge bg-primary">
                                  {client.planType || "N/A"}
                                </span>
                              </td>
                              <td>{client.connects}</td>
                              <td>{formatDate(client.endDate)}</td>
                              <td>
                                <span
                                  className={`badge ${
                                    client.isActive ? "bg-success" : "bg-danger"
                                  }`}
                                >
                                  {client.isActive ? "Active" : "Inactive"}
                                </span>
                              </td>
                              <td>
                                <ul className="list-inline hstack gap-2 mb-0">
                                  <li
                                    className="list-inline-item"
                                    data-bs-toggle="tooltip"
                                    data-bs-trigger="hover"
                                    data-bs-placement="top"
                                    title="Edit"
                                  >
                                    <Link
                                      className="edit-item-btn"
                                      onClick={(e) => handleEditClick(client)}
                                    >
                                      <i className="ri-pencil-fill align-bottom text-muted" />
                                    </Link>
                                  </li>
                                  <li
                                    className="list-inline-item"
                                    data-bs-toggle="tooltip"
                                    data-bs-trigger="hover"
                                    data-bs-placement="top"
                                    title="Delete"
                                  >
                                    <Link
                                      className="remove-item-btn"
                                      onClick={(e) =>
                                        handleOpenDeleteModal(client.companyId)
                                      }
                                    >
                                      <i className="ri-delete-bin-fill align-bottom text-muted" />
                                    </Link>
                                  </li>
                                  {/* KEY BUTTON */}
                                  <li
                                    className="list-inline-item"
                                    data-bs-toggle="tooltip"
                                    data-bs-trigger="hover"
                                    data-bs-placement="top"
                                    title="API Keys"
                                  >
                                    <Link
                                      className="remove-item-btn"
                                      onClick={(e) => handleKeyClick(client)}
                                    >
                                      <i className="ri-key-fill align-bottom text-primary" />
                                    </Link>
                                  </li>
                                </ul>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="8" className="text-center py-4">
                              No B2E clients found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Pagination section */}
                {filteredClients.length > 0 && (
                  <div className="align-items-center mt-4 pt-2 justify-content-between d-flex">
                    <div className="flex-shrink-0">
                      <div className="text-muted">
                        Showing{" "}
                        <span className="fw-semibold">
                          {filteredClients.length}
                        </span>{" "}
                        of <span className="fw-semibold">{totalRecords}</span>{" "}
                        Results
                      </div>
                    </div>
                    <ul className="pagination pagination-separated pagination-sm mb-0">
                      <li className="page-item" onClick={GetFirstPageData}>
                        <Link className="page-link">Previous</Link>
                      </li>
                      {renderPageNumbers}
                      <li className="page-item" onClick={GetLastPageData}>
                        <Link className="page-link">Next</Link>
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Edit/Add Client Modal */}
        <Modal
          show={editShow}
          onHide={handleEditClose}
          size="lg"
          centered
          scrollable
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {editClient.companyId
                ? `Edit B2E Client - ${editClient.companyName}`
                : "Add B2E Client"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
            <div className="container-fluid">
              {/* Company Logo */}
              <div className="row mb-4">
                <label className="col-sm-3 col-form-label">Company Logo:</label>
                <div className="col-sm-9">
                  <div className="d-flex align-items-center gap-3">
                    {logoPreview ? (
                      <img
                        src={logoPreview}
                        alt="Company Logo"
                        className="img-thumbnail"
                        style={{
                          width: "120px",
                          height: "120px",
                          objectFit: "contain",
                        }}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    ) : (
                      <div
                        className="img-thumbnail d-flex align-items-center justify-content-center"
                        style={{
                          width: "120px",
                          height: "120px",
                          backgroundColor: "#f8f9fa",
                          border: "1px solid #dee2e6",
                        }}
                      >
                        <i className="ri-building-2-line text-muted fs-2"></i>
                      </div>
                    )}
                    {editShow && (
                      <div className="flex-grow-1">
                        <input
                          type="file"
                          className="form-control"
                          accept="image/png, image/jpeg, image/gif"
                          onChange={handleFileChange}
                        />
                        <div className="form-text">
                          Recommended size: 120x120px. Supports PNG, JPG, GIF.
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Company Name */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">Company Name:</label>
                <div className="col-sm-9">
                  <input
                    type="text"
                    className={`form-control ${
                      formErrors.companyName ? "is-invalid" : ""
                    }`}
                    value={editClient.companyName || ""}
                    maxLength={40}
                    name="companyName"
                    onChange={handleEditInputChange}
                    placeholder="Enter company name"
                  />
                  {formErrors.companyName && (
                    <div className="invalid-feedback">
                      {formErrors.companyName}
                    </div>
                  )}
                </div>
              </div>

              {/* Brand Name */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">Brand Name:</label>
                <div className="col-sm-9">
                  <input
                    type="text"
                    className={`form-control ${
                      formErrors.brandName ? "is-invalid" : ""
                    }`}
                    value={editClient.brandName || ""}
                    maxLength={40}
                    name="brandName"
                    onChange={handleEditInputChange}
                    placeholder="Enter brand name"
                  />
                  {formErrors.brandName && (
                    <div className="invalid-feedback">
                      {formErrors.brandName}
                    </div>
                  )}
                </div>
              </div>

              {/* Email */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">Email:</label>
                <div className="col-sm-9">
                  <input
                    type="email"
                    className={`form-control ${
                      formErrors.email ? "is-invalid" : ""
                    }`}
                    value={editClient.email || ""}
                    maxLength={40}
                    name="email"
                    onChange={handleEditInputChange}
                    placeholder="Enter email address"
                  />
                  {formErrors.email && (
                    <div className="invalid-feedback">{formErrors.email}</div>
                  )}
                </div>
              </div>

              {/* Phone */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">Phone:</label>
                <div className="col-sm-9">
                  <input
                    type="text"
                    className={`form-control ${
                      formErrors.phoneNumber ? "is-invalid" : ""
                    }`}
                    value={editClient.phoneNumber || ""}
                    maxLength={15}
                    name="phoneNumber"
                    onChange={handleEditInputChange}
                    placeholder="Enter phone number"
                  />
                  {formErrors.phoneNumber && (
                    <div className="invalid-feedback">
                      {formErrors.phoneNumber}
                    </div>
                  )}
                </div>
              </div>

              {/* City */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">City:</label>
                <div className="col-sm-9">
                  <input
                    type="text"
                    className="form-control"
                    value={editClient.city || ""}
                    maxLength={40}
                    name="city"
                    onChange={handleEditInputChange}
                    placeholder="Enter city"
                  />
                </div>
              </div>

              {/* State */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">State:</label>
                <div className="col-sm-9">
                  <input
                    type="text"
                    className="form-control"
                    value={editClient.state || ""}
                    maxLength={40}
                    name="state"
                    onChange={handleEditInputChange}
                    placeholder="Enter state"
                  />
                </div>
              </div>

              {/* Address */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">Address:</label>
                <div className="col-sm-9">
                  <textarea
                    className="form-control"
                    value={editClient.address || ""}
                    maxLength={250}
                    name="address"
                    onChange={handleEditInputChange}
                    placeholder="Enter address"
                    rows="3"
                  />
                </div>
              </div>

             {/* Country */}
<div className="row mb-3">
  <label className="col-sm-3 col-form-label">Country:</label>
  <div className="col-sm-9">
    <div className="d-flex align-items-center gap-2">
      {/* Show flag of selected country next to dropdown */}
      {editClient.country && countries.find(c => c.countryCode === editClient.country)?.flag && (
        <img 
          src={countries.find(c => c.countryCode === editClient.country).flag} 
          alt="flag" 
          style={{ width: "30px", height: "20px", border: "1px solid #eee" }} 
        />
      )}
      <select
        className="form-select"
        value={editClient.country || ""}
        name="country"
        onChange={(e) => handleCountryChange(e.target.value)}
      >
        <option value="">Select Country</option>
        {countries.map((country) => (
          <option
            key={country.countryCode}
            value={country.countryCode}
          >
            {country.country} ({country.countryCode})
          </option>
        ))}
      </select>
    </div>
  </div>
</div>

             {/* Currency */}
<div className="row mb-3">
  <label className="col-sm-3 col-form-label">Currency:</label>
  <div className="col-sm-9">
    <div className="d-flex align-items-center gap-2">
      {/* Show flag of selected currency next to dropdown */}
      {editClient.currency && countries.find(c => c.code === editClient.currency)?.flag && (
        <img 
          src={countries.find(c => c.code === editClient.currency).flag} 
          alt="flag" 
          style={{ width: "30px", height: "20px", border: "1px solid #eee" }} 
        />
      )}
      <select
        className="form-select"
        value={editClient.currency || ""}
        onChange={(e) => handleCurrencyChange(e.target.value)}
      >
        <option value="">Select Currency</option>
        {currencies.map((curr) => (
          <option key={curr.code} value={curr.code}>
            {curr.code} - {curr.name}
          </option>
        ))}
      </select>
    </div>
  </div>
</div>

              {/* GST No / Tax No - Conditionally rendered based on country */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">
                  {isIndia(editClient.country) ? "GST No:" : "Tax No:"}
                </label>
                <div className="col-sm-9">
                  <input
                    type="text"
                    className="form-control"
                    value={editClient.gstNo || ""}
                    maxLength={15}
                    name="gstNo"
                    onChange={handleEditInputChange}
                    placeholder={
                      isIndia(editClient.country)
                        ? "Enter GST number"
                        : "Enter tax number"
                    }
                  />
                </div>
              </div>

              {/* Pan Card - Only show for India */}
              {isIndia(editClient.country) && (
                <div className="row mb-3">
                  <label className="col-sm-3 col-form-label">Pan Card:</label>
                  <div className="col-sm-9">
                    <input
                      type="text"
                      className="form-control"
                      value={editClient.panCard || ""}
                      maxLength={15}
                      name="panCard"
                      onChange={handleEditInputChange}
                      placeholder="Enter PAN card number"
                    />
                  </div>
                </div>
              )}

              {/* WhatsApp Number */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">
                  WhatsApp Number:
                </label>
                <div className="col-sm-9">
                  <input
                    type="text"
                    className="form-control"
                    value={editClient.whatsAppNumber || ""}
                    maxLength={15}
                    name="whatsAppNumber"
                    onChange={handleEditInputChange}
                    placeholder="Enter WhatsApp number"
                  />
                </div>
              </div>

              {/* Primary Domain */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">
                  Primary Domain:
                </label>
                <div className="col-sm-9">
                  <input
                    type="text"
                    className="form-control"
                    value={editClient.primaryDomain || ""}
                    maxLength={100}
                    name="primaryDomain"
                    onChange={handleEditInputChange}
                    placeholder="e.g., https://example.com"
                  />
                </div>
              </div>

              {/* Plan Type */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">Plan Type:</label>
                <div className="col-sm-9">
                  <select
                    className="form-select"
                    value={editClient.planType || "TRAIL"}
                    name="planType"
                    onChange={handleEditInputChange}
                  >
                    <option value="TRAIL">TRAIL</option>
                    <option value="MONTHLYSTARTER">MONTHLYSTARTER</option>
                    <option value="MONTHLYPRO">MONTHLYPRO</option>
                    <option value="MONTHLYENTERPRISE">MONTHLYENTERPRISE</option>
                  </select>
                </div>
              </div>

              {/* Connects */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">Connects:</label>
                <div className="col-sm-9">
                  <input
                    type="number"
                    className="form-control"
                    value={editClient.connects || 0}
                    name="connects"
                    onChange={handleEditInputChange}
                    placeholder="Enter number of connects"
                  />
                </div>
              </div>

              {/* End Date */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">End Date:</label>
                <div className="col-sm-9">
                  <input
                    type="datetime-local"
                    className="form-control"
                    value={
                      editClient.endDate
                        ? new Date(editClient.endDate)
                            .toISOString()
                            .slice(0, 16)
                        : ""
                    }
                    name="endDate"
                    onChange={handleEditInputChange}
                  />
                </div>
              </div>

              {/* Status */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">Status:</label>
                <div className="col-sm-9">
                  <select
                    className="form-select"
                    value={editClient.isActive}
                    name="isActive"
                    onChange={handleEditInputChange}
                  >
                    <option value={true}>Active</option>
                    <option value={false}>Inactive</option>
                  </select>
                </div>
              </div>

              {/* Separated Features Section */}
              <div className="row mb-3">
                <div className="col-12">
                  <h6 className="border-bottom pb-2 mb-3">Features</h6>
                </div>
              </div>

              {/* B2B Feature */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">Is B2B:</label>
                <div className="col-sm-9">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      checked={editClient.isB2B || false}
                      name="isB2B"
                      onChange={handleEditInputChange}
                    />
                    <label className="form-check-label">B2B Enabled</label>
                  </div>
                </div>
              </div>

              {/* B2C Feature */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">Is B2C:</label>
                <div className="col-sm-9">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      checked={editClient.isB2C || false}
                      name="isB2C"
                      onChange={handleEditInputChange}
                    />
                    <label className="form-check-label">B2C Enabled</label>
                  </div>
                </div>
              </div>

              {/* B2E Feature */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">Is B2E:</label>
                <div className="col-sm-9">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      checked={editClient.isB2E || false}
                      name="isB2E"
                      onChange={handleEditInputChange}
                    />
                    <label className="form-check-label">B2E Enabled</label>
                  </div>
                </div>
              </div>

              {/* Website Feature */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">Website:</label>
                <div className="col-sm-9">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      checked={editClient.isWebsite || false}
                      name="isWebsite"
                      onChange={handleEditInputChange}
                    />
                    <label className="form-check-label">
                      Enable Website Feature
                    </label>
                  </div>
                </div>
              </div>

              {/* Chat Bot Feature */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">Chat Bot:</label>
                <div className="col-sm-9">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      checked={editClient.isChatBot || false}
                      name="isChatBot"
                      onChange={handleEditInputChange}
                    />
                    <label className="form-check-label">
                      Enable Chat Bot Feature
                    </label>
                  </div>
                </div>
              </div>

              {/* Supplier API Feature */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">Supplier API:</label>
                <div className="col-sm-9">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      checked={editClient.isSupplierAPI || false}
                      name="isSupplierAPI"
                      onChange={handleEditInputChange}
                    />
                    <label className="form-check-label">
                      Enable Supplier API Feature
                    </label>
                  </div>
                </div>
              </div>

              {/* Renewal Feature */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">Renewal:</label>
                <div className="col-sm-9">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      checked={editClient.isRenewal || false}
                      name="isRenewal"
                      onChange={handleEditInputChange}
                    />
                    <label className="form-check-label">
                      Enable Auto Renewal
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className="d-flex justify-content-center w-100">
              {editBtnLoading ? (
                <button className="btn btn-success me-2 px-4" disabled>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  {editClient.companyId ? "Saving..." : "Creating..."}
                </button>
              ) : (
                <button
                  className="btn btn-success me-2 px-4"
                  onClick={handleUpdateClient}
                >
                  <i className="fas fa-save me-2"></i>
                  {editClient.companyId ? "Save Changes" : "Create Client"}
                </button>
              )}
              <button className="btn btn-danger px-4" onClick={handleEditClose}>
                <i className="fas fa-times me-2"></i>Cancel
              </button>
            </div>
          </Modal.Footer>
        </Modal>

        {/* API Details Modal */}
        <Modal show={apiShow} onHide={handleAPIClose} size="xl" scrollable>
          <Modal.Header closeButton>
            <Modal.Title>
              Client API Keys - {selectedClient?.companyName}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {/* Info Alert */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="alert alert-info mb-0">
                  <i className="ri-information-line me-1"></i>
                  Managing API details for client:{" "}
                  <strong>{selectedClient?.companyName}</strong>
                </div>
              </div>
            </div>

            {loadingSuppliers ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading supplier APIs...</p>
              </div>
            ) : (
              <form onSubmit={handleApiSubmit} autoComplete="off" noValidate>
                {renderSupplierForm()}

                <Modal.Footer>
                  <div className="hstack gap-2 justify-content-end mb-3 mt-4">
                    {apiBtnLoading === false ? (
                      <button type="submit" className="btn btn-primary">
                        Save API Key
                      </button>
                    ) : (
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled
                      >
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Saving...
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={handleAPIClose}
                    >
                      Close
                    </button>
                  </div>
                </Modal.Footer>
              </form>
            )}
          </Modal.Body>
        </Modal>

        {/* Delete Confirmation Modal */}
        <DeleteAlert
          show={showDeleteModal}
          handleClose={handleCloseDeleteModal}
          handleDelete={handleDelete}
        />
      </div>
    </>
  );
};

export default B2ECompanies;
